/**
 * CreatorSpotlight — Professional creator spotlight for homepage.
 * Shows profile picture, bio, and top track with smooth transitions.
 * Mobile-optimized layout.
 */
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, Music, Users, Play,
  Disc, Star, TrendingUp, Crown
} from 'lucide-react';
import axios from 'axios';
import { useAudio } from '../context/AudioContext';
import { useTheme } from '../context/ThemeContext';

import { getProfilePicUrl } from '../utils/profilePic';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TYPE_STYLES = {
  'Artist':    { bg: 'from-cyan-500/20 to-cyan-500/5', border: 'border-cyan-500/30', text: 'text-cyan-400' },
  'Producer':  { bg: 'from-emerald-500/20 to-emerald-500/5', border: 'border-emerald-500/30', text: 'text-emerald-400' },
  'AI Artist': { bg: 'from-rose-500/20 to-rose-500/5', border: 'border-rose-500/30', text: 'text-rose-400' },
  'DJ':        { bg: 'from-orange-500/20 to-orange-500/5', border: 'border-orange-500/30', text: 'text-orange-400' },
  'Promoter':  { bg: 'from-purple-500/20 to-purple-500/5', border: 'border-purple-500/30', text: 'text-purple-400' },
};

function ProfilePic({ creator, className = '' }) {
  const [err, setErr] = useState(false);
  // Use getProfilePicUrl which maps to the fast /api/profile/picture/{filename} endpoint
  const src = getProfilePicUrl(creator.profile_picture, creator.id);

  // Reset error state when creator changes
  useEffect(() => {
    setErr(false);
  }, [creator.id]);

  if (!src || err) {
    return (
      <div className={`rounded-full bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] border-[3px] border-white/30 flex items-center justify-center shrink-0 ${className}`}>
        <span className="text-3xl md:text-4xl font-['Chakra_Petch'] text-[#555]">
          {(creator.username || '?')[0].toUpperCase()}
        </span>
      </div>
    );
  }

  return (
    <img
      src={src} alt={creator.username}
      className={`rounded-full object-cover shrink-0 ${className}`}
      loading="eager"
      decoding="async"
      onError={() => setErr(true)}
    />
  );
}

export default function CreatorSpotlight() {
  const [creators, setCreators] = useState([]);
  const [idx, setIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [fade, setFade] = useState(true);
  const intervalRef = useRef(null);
  const navigate = useNavigate();
  const { playTrack } = useAudio();
  const { mode } = useTheme();
  const isLight = mode === 'light';

  useEffect(() => {
    axios.get(`${API}/spotlight/artists?limit=8&include_types=all`)
      .then(res => {
        const data = res.data || [];
        setCreators(data);
        // Preload all profile pictures for instant switching
        data.forEach(c => {
          const url = getProfilePicUrl(c.profile_picture, c.id);
          if (url) {
            const img = new Image();
            img.src = url;
          }
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (creators.length > 1 && !isPaused) {
      intervalRef.current = setInterval(() => switchTo((idx + 1) % creators.length), 6000);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [creators.length, isPaused, idx]);

  const switchTo = (newIdx) => {
    setFade(false);
    setTimeout(() => {
      setIdx(newIdx);
      setFade(true);
    }, 200);
  };

  const goToPrev = () => {
    setIsPaused(true);
    switchTo((idx - 1 + creators.length) % creators.length);
    setTimeout(() => setIsPaused(false), 10000);
  };

  const goToNext = () => {
    setIsPaused(true);
    switchTo((idx + 1) % creators.length);
    setTimeout(() => setIsPaused(false), 10000);
  };

  const goTo = (i) => {
    setIsPaused(true);
    switchTo(i);
    setTimeout(() => setIsPaused(false), 10000);
  };

  if (loading) {
    return (
      <section className="px-4 md:px-8 py-8">
        <div className="h-40 bg-[#0a0a0a] border border-[#1a1a1a] animate-pulse" />
      </section>
    );
  }

  if (creators.length === 0) return null;

  const c = creators[idx];
  if (!c) return null;
  const style = TYPE_STYLES[c.creator_type] || TYPE_STYLES['Artist'];

  const handlePlay = (e) => {
    e.stopPropagation();
    const ft = c.featured_track;
    if (ft && (ft.filename || ft.r2_key || ft.id)) {
      playTrack(
        {
          id: ft.id,
          title: ft.title,
          artist: ft.artist || c.username,
          artwork: ft.artwork,
          filename: ft.filename,
          r2_key: ft.r2_key,
          is_free: ft.is_free,
          price: ft.price,
          preview_start: ft.preview_start || 0,
          user_id: c.id,
          username: c.username,
        },
        [],
        { isOwner: false, hasPurchased: false }
      );
    }
  };

  return (
    <section className="px-4 md:px-8 py-8" data-testid="creator-spotlight-section">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="font-mono text-[10px] md:text-xs uppercase tracking-[0.2em] text-[#d4a843]">Featured Creators</p>
          <h2 className="text-xl md:text-2xl font-['Chakra_Petch'] uppercase mt-0.5">
            <span className="bg-gradient-to-r from-[#d4a843] to-[#f0d68a] bg-clip-text text-transparent">Creator Spotlight</span>
          </h2>
        </div>

        {creators.length > 1 && (
          <div className="flex items-center gap-1.5">
            <button
              onClick={goToPrev}
              className="w-8 h-8 border border-[#d4a843]/30 flex items-center justify-center text-[#d4a843]/60 hover:text-[#d4a843] hover:border-[#d4a843] transition-colors"
              data-testid="spotlight-prev"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="font-mono text-xs w-10 text-center text-[#d4a843]/50">
              {idx + 1}/{creators.length}
            </span>
            <button
              onClick={goToNext}
              className="w-8 h-8 border border-[#d4a843]/30 flex items-center justify-center text-[#d4a843]/60 hover:text-[#d4a843] hover:border-[#d4a843] transition-colors"
              data-testid="spotlight-next"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        )}
      </div>

      {/* Gold Plated Wide Card */}
      <div
        className={`transition-opacity duration-300 ${fade ? 'opacity-100' : 'opacity-0'}`}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        data-testid={`spotlight-card-${c.id}`}
      >
        <div 
          className="relative overflow-hidden border-2 border-[#d4a843]/40 hover:border-[#d4a843] transition-all"
          style={{
            background: 'linear-gradient(135deg, rgba(212,168,67,0.08) 0%, #0a0a0a 25%, #0a0a0a 75%, rgba(212,168,67,0.08) 100%)',
            boxShadow: '0 0 20px rgba(212,168,67,0.08), inset 0 1px 0 rgba(212,168,67,0.2)'
          }}
        >
          {/* Gold accent top line */}
          <div className="h-0.5 bg-gradient-to-r from-transparent via-[#d4a843] to-transparent" />

          {/* Wide horizontal layout */}
          <div className="flex flex-col md:flex-row items-center p-5 md:p-8 gap-6">
            {/* Profile pic with gold border */}
            <div className="relative cursor-pointer flex-shrink-0" onClick={() => navigate(`/profile/${c.id}`)}>
              <div className="w-28 h-28 md:w-36 md:h-36 p-1 bg-gradient-to-br from-[#d4a843] via-[#f0d68a] to-[#8b6914]">
                <ProfilePic creator={c} className="w-full h-full !border-0" />
              </div>
              {c.is_vip && (
                <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1 px-2.5 py-0.5 bg-gradient-to-r from-[#d4a843] to-[#f0d68a] text-black text-[9px] font-mono font-bold uppercase tracking-wider whitespace-nowrap" data-testid={`vip-badge-${c.id}`}>
                  <Crown size={10} /> VIP
                </div>
              )}
            </div>

            {/* Info section */}
            <div className="flex-1 text-center md:text-left min-w-0">
              {/* Badges */}
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-1.5 mb-3">
                <span className="px-2 py-0.5 text-[#d4a843] border border-[#d4a843]/30 bg-[#d4a843]/5 font-mono text-[9px] uppercase tracking-wider">
                  {c.creator_type}
                </span>
                {c.is_admin_featured && (
                  <span className="px-2 py-0.5 text-[#f0d68a] border border-[#f0d68a]/30 bg-[#f0d68a]/5 font-mono text-[9px] uppercase flex items-center gap-1">
                    <Star size={9} /> Featured
                  </span>
                )}
              </div>

              <h3
                className="text-xl md:text-2xl font-['Chakra_Petch'] uppercase font-bold cursor-pointer hover:opacity-80 transition-opacity truncate"
                onClick={() => navigate(`/profile/${c.id}`)}
              >
                <span className="bg-gradient-to-r from-[#d4a843] to-[#f0d68a] bg-clip-text text-transparent">{c.username}</span>
              </h3>

              {/* Stats bar — bolder + brighter for readability */}
              <div className="flex items-center justify-center md:justify-start gap-4 mt-3 text-[11px] md:text-sm text-white font-mono font-bold bg-white/95 px-4 py-2 rounded-md w-full md:w-fit shadow-sm">
                <span className="flex items-center gap-1.5 text-[#1a1a1a]"><Users size={13} className="text-[#d4a843]" /> <strong>{c.followers_count || 0}</strong> followers</span>
                <span className="flex items-center gap-1.5 text-[#1a1a1a]"><Music size={13} className="text-[#d4a843]" /> <strong>{c.track_count || 0}</strong> tracks</span>
                <span className="flex items-center gap-1.5 text-[#1a1a1a]"><Play size={13} className="text-[#d4a843]" /> <strong>{(c.total_plays || 0).toLocaleString()}</strong> plays</span>
              </div>
            </div>

            {/* Featured track */}
            {c.featured_track && (
              <div className="flex items-center gap-3 p-3 bg-black/40 border border-[#d4a843]/15 flex-shrink-0 w-full md:w-auto md:min-w-[280px]">
                {c.featured_track.artwork ? (
                  <img
                    src={c.featured_track.artwork.startsWith('http') ? c.featured_track.artwork : `${BACKEND_URL}${c.featured_track.artwork}`}
                    alt="" className="w-12 h-12 object-cover shrink-0 border border-[#d4a843]/20"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <div className="w-12 h-12 bg-[#111] flex items-center justify-center shrink-0 border border-[#d4a843]/20">
                    <Disc size={18} className="text-[#d4a843]/30" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-[#f0d68a] text-xs font-mono truncate">{c.featured_track.title}</p>
                  <p className="text-[#666] text-[10px] font-mono">Top Track</p>
                </div>
                <button
                  onClick={handlePlay}
                  className="w-10 h-10 flex items-center justify-center border border-[#d4a843]/30 text-[#d4a843] hover:bg-[#d4a843]/10 transition-colors shrink-0"
                  data-testid={`play-featured-${c.id}`}
                >
                  <Play size={14} />
                </button>
              </div>
            )}
          </div>

          {/* Gold accent bottom */}
          <div className="h-px bg-gradient-to-r from-transparent via-[#d4a843]/40 to-transparent" />
        </div>
      </div>

      {/* Dots */}
      {creators.length > 1 && (
        <div className="flex items-center justify-center gap-1.5 mt-3">
          {creators.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              className={`h-1 transition-all duration-300 ${
                i === idx ? 'w-5 bg-[#d4a843]' : 'w-1.5 bg-[#333] hover:bg-[#555]'
              }`}
              data-testid={`spotlight-dot-${i}`}
            />
          ))}
        </div>
      )}
    </section>
  );
}
